import { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DisplayHubDetails from 'src/components/DisplayHubDetails';
import RundownDialog, { DialogTypes } from 'src/components/RundownDialog';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle as ResizeHandle,
} from 'src/components/ResizeablePanel';
import DisplayHubs from 'src/components/DisplayHubs';
import Playlists from 'src/components/Playlists';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { StateType } from 'src/redux/reducers';
import { REQUEST_CREATE_PLAYLIST } from 'src/redux/reducers/playlist';
import { REQUEST_CREATE_PLAYLIST_DISPLAY_HUB } from 'src/redux/reducers/playlistDisplayHub';
import { REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY } from 'src/redux/reducers/playlistDisplayHubStory';

function LayoutDisplayHub() {
  const dispatch = useDispatch();

  const { data } = useSelector((state: StateType) => state.rundown.detail);
  const { selectedId: activePlaylistId } = useSelector((state: StateType) => state.playlist.getAll);
  const { selectedPlaylistDisplayHub } = useSelector(
    (state: StateType) => state.playlistDisplayHub
  );

  const guaranteedActivePlaylistId = useRef<string | null>(activePlaylistId);
  const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);

  useEffect(() => {
    guaranteedActivePlaylistId.current = activePlaylistId;
  }, [activePlaylistId]);

  if (!data) return null;

  const handleRundownDialog = (name: string) => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId: data.id, name } });
        break;
      case DialogTypes.DISPLAY_HUB:
        dispatch({
          type: REQUEST_CREATE_PLAYLIST_DISPLAY_HUB,
          payload: { playlistId: guaranteedActivePlaylistId.current, name },
        });
        break;
      case DialogTypes.DISPLAY_HUB_STORY:
        if (!selectedPlaylistDisplayHub) return;
        dispatch({
          type: REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY,
          payload: { rundownId: data.id, playlistDisplayHubId: selectedPlaylistDisplayHub.id, name },
        });
        break;
    }
    setShowDialog(null);
  };

  const getDialogMeta = () => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        return { title: 'Create Playlist', subtitle: 'Enter name for the playlist' };
      case DialogTypes.DISPLAY_HUB:
        return { title: 'Create Display Hub', subtitle: 'Enter name for the display hub' };
      case DialogTypes.DISPLAY_HUB_STORY:
        return { title: 'Create Display Hub Story', subtitle: 'Enter name for the display hub story' };
      default:
        return { title: '', subtitle: '' };
    }
  };

  const canvases = data.canvases.filter((canvas) =>
    canvas.graphics.some((g) => g.types.includes('displayHub'))
  );

  return (
    <div className="h-full">
      <RundownDialog
        dialogTitle={getDialogMeta().title}
        dialogSubtitle={getDialogMeta().subtitle}
        isOpen={!!showDialog}
        onClose={() => setShowDialog(null)}
        onSubmit={handleRundownDialog}
      />
      <PanelGroup direction="vertical">
        <Panel defaultSize={32} className="!bg-default-surface">
          <PanelGroup direction="horizontal">
            {canvases.map((canvas) => (
              <ViewWindow
                key={canvas.id}
                name={canvas.name}
                src={canvas.output?.liveUrl}
                liveMode
              />
            ))}
          </PanelGroup>
        </Panel>

        <ResizeHandle />

        <Panel>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={20}>
              <PanelGroup direction="vertical">
                <Panel
                  header="Playlists"
                  icon="plus"
                  onClick={() => setShowDialog(DialogTypes.PLAYLIST)}
                  defaultSize={25}
                >
                  <Playlists rundownId={data.id} />
                </Panel>

                <ResizeHandle />

                <Panel
                  header="Display Hubs"
                  icon="plus"
                  onClick={() => setShowDialog(DialogTypes.DISPLAY_HUB)}
                >
                  <DisplayHubs />
                </Panel>
              </PanelGroup>
            </Panel>
            <ResizeHandle />
            <Panel>
              {selectedPlaylistDisplayHub && (
                <DisplayHubDetails
                  handleCreateStory={() => setShowDialog(DialogTypes.DISPLAY_HUB_STORY)}
                />
              )}
            </Panel>
          </PanelGroup>
        </Panel>
      </PanelGroup>
    </div>
  );
}

export default LayoutDisplayHub;
